import { useState } from "react";
import { Pagination, Select } from "antd";
import { Bar } from "../states";
import { sortByColumn } from "../../utils/tableSort";

const NO_SORT = "none";

const toSortValue = ({ columnKey, order } = {}) =>
  columnKey && order ? `${columnKey}:${order}` : NO_SORT;

// Every column the table can sort by, both ways — so the phone offers exactly
// the sorts the desktop headers do, no more and no fewer.
export const sortOptionsFor = (columns) =>
  columns
    .filter((column) => column.sorter && typeof column.title === "string")
    .flatMap((column) => {
      const key = column.key ?? column.dataIndex;
      return [
        { label: `${column.title} ↑`, value: `${key}:ascend` },
        { label: `${column.title} ↓`, value: `${key}:descend` },
      ];
    });

// The sort a table starts with: the column carrying `defaultSortOrder`.
const defaultSortOf = (columns) => {
  const column = (columns ?? []).find((c) => c.defaultSortOrder);
  return column
    ? { columnKey: column.key ?? column.dataIndex, order: column.defaultSortOrder }
    : {};
};

const fromSortValue = (value) => {
  if (value === NO_SORT) return {};
  const [columnKey, order] = value.split(":");
  return { columnKey, order };
};

// Placeholder for one card while the first page loads: avatar + two lines,
// two body lines, footer line + buttons — the shape every list card shares.
const SkeletonCard = () => (
  <div className="flex flex-col gap-3 rounded-[10px] border border-gray-200 bg-white px-3.5 py-3">
    <div className="flex items-center gap-2.5">
      <Bar w={36} h={36} r={18} className="shrink-0" />
      <div className="flex flex-1 flex-col gap-1.5">
        <Bar w="55%" h={14} />
        <Bar w="40%" h={10} />
      </div>
    </div>
    <Bar w="70%" h={11} />
    <div className="flex items-center justify-between border-t border-gray-100 pt-2">
      <Bar w="45%" h={10} />
      <Bar w={112} h={32} />
    </div>
  </div>
);

/**
 * The phone stand-in for an antd <Table>: one card per record, with the
 * table's empty state and paging.
 *
 * Paging: by default the list pages `items` itself, 10 a page; `resetKey`
 * should change with the page's search and filters so the list goes back to
 * page 1, as antd's Table does. A server-paged screen passes `pagination`
 * ({ current, pageSize, total, onChange }) and the current page's rows.
 * `showTotal` is antd Pagination's, for screens whose table shows a total.
 *
 * Sorting (phones have no column headers): pass the table's `columns` and
 * the list shows a "Sort by" select with each sortable column both ways.
 * Without `onSortChange` the cards are ordered here with those columns' own
 * sorters; with `sort` ({ columnKey, order }) and `onSortChange` the page (or
 * the server) orders `items` itself.
 */
const CardList = ({
  items,
  rowKey,
  renderItem,
  loading = false,
  empty = null,
  pageSize = 10,
  label,
  resetKey,
  pagination,
  columns,
  sort: controlledSort,
  onSortChange,
  showTotal,
  className = "",
}) => {
  const sortOptions = columns ? sortOptionsFor(columns) : [];
  const [page, setPage] = useState(1);
  const [localSort, setLocalSort] = useState(() => defaultSortOf(columns));
  const sort = onSortChange ? controlledSort ?? {} : localSort;

  const pageKey = `${resetKey}|${toSortValue(sort)}`;
  const [seenPageKey, setSeenPageKey] = useState(pageKey);
  if (pageKey !== seenPageKey) {
    setSeenPageKey(pageKey);
    setPage(1);
  }

  if (loading && !items.length) {
    return (
      <div
        className={`flex flex-col gap-2.5 ${className}`}
        aria-busy="true"
        aria-label={label}
      >
        {Array.from({ length: 3 }, (_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (!items.length) return <div className={className}>{empty}</div>;

  let rows = items;
  let pager = null;
  if (pagination) {
    pager = pagination.total > pagination.pageSize && (
      <Pagination className="self-center" showSizeChanger={false} {...pagination} />
    );
  } else {
    if (columns && !onSortChange) rows = sortByColumn(items, columns, sort);
    // A search or filter can shrink the list under the current page.
    const lastPage = Math.max(1, Math.ceil(rows.length / pageSize));
    const current = Math.min(page, lastPage);
    const start = (current - 1) * pageSize;
    pager = rows.length > pageSize && (
      <Pagination
        className="self-center"
        current={current}
        pageSize={pageSize}
        total={rows.length}
        showTotal={showTotal}
        showSizeChanger={false}
        onChange={setPage}
      />
    );
    rows = rows.slice(start, start + pageSize);
  }

  return (
    <div className={`flex flex-col gap-4 ${className}`} aria-busy={loading || undefined}>
      {sortOptions.length > 0 && (
        <div className="flex items-center justify-end gap-2 text-sm text-gray-600">
          <span aria-hidden="true">Sort by</span>
          <Select
            aria-label={`Sort ${label}`}
            className="min-w-[150px]"
            value={toSortValue(sort)}
            onChange={(value) => (onSortChange ?? setLocalSort)(fromSortValue(value))}
            options={[{ label: "Default order", value: NO_SORT }, ...sortOptions]}
          />
        </div>
      )}
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0" aria-label={label}>
        {rows.map((item) => (
          <li key={rowKey(item)}>{renderItem(item)}</li>
        ))}
      </ul>
      {pager}
    </div>
  );
};

export default CardList;
